import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { API_BASE_URL } from "./apiClient";

const INTERVAL_KEY = "fp:appUpdate:intervalHours";
const SUCCESS_KEY = "fp:appUpdate:lastSuccessfulCheckAt:v2";
const FAILURE_KEY = "fp:appUpdate:lastFailureAt:v2";
const CACHE_KEY = "fp:appUpdate:lastConfig:v2";
const SNOOZE_KEY = "fp:appUpdate:snooze:v2";
const MANUAL_TRIGGER_KEY = "fp:appUpdate:handledManualTriggerId";
const DEFAULT_INTERVAL_HOURS = 24;
const MAX_INTERVAL_HOURS = 24 * 30;
const FAILURE_RETRY_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 10 * 1000;

let activeCheckPromise = null;

function readValue(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function readNumber(key, fallback) {
  const raw = readValue(key);
  if (raw === null || raw === "") return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function readJson(key) {
  try {
    const raw = readValue(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeValue(key, value) {
  try { localStorage.setItem(key, String(value)); } catch {}
}

function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function removeValue(key) {
  try { localStorage.removeItem(key); } catch {}
}

function normalizedInterval(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_INTERVAL_HOURS;
  return Math.min(MAX_INTERVAL_HOURS, Math.max(0, parsed));
}

function versionParts(value) {
  return String(value || "")
    .trim()
    .split(/[.+-]/)
    .map((part) => Number.parseInt(part.match(/^\d+/)?.[0] || "0", 10));
}

export function compareAppVersions(left, right) {
  const a = versionParts(left);
  const b = versionParts(right);
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (a[index] || 0) - (b[index] || 0);
    if (difference !== 0) return difference > 0 ? 1 : -1;
  }
  return 0;
}

function numericBuild(value) {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function appIdentity(platform, appInfo) {
  return {
    platform,
    version: String(appInfo?.version || "0"),
    build: String(appInfo?.build || "0"),
  };
}

function sameAppIdentity(left, right) {
  return left?.platform === right?.platform &&
    String(left?.version || "") === String(right?.version || "") &&
    String(left?.build || "") === String(right?.build || "");
}

function normalizeConfig(data, identity) {
  const intervalHours = normalizedInterval(data?.checkIntervalHours);
  const reason = data?.reason === "manual" ? "manual" : data?.reason === "version" ? "version" : "none";
  const latestVersion = String(data?.latestVersion || "").trim();
  const latestBuild = numericBuild(data?.latestBuild);
  const manualTriggerId = String(data?.manualTriggerId || "");
  const fingerprint = String(data?.configRevision || [
    identity.platform,
    latestVersion || identity.version,
    latestBuild,
    reason,
    manualTriggerId,
    data?.required === true ? "required" : "optional",
  ].join(":"));

  return {
    enabled: data?.enabled === true,
    updateAvailable: data?.updateAvailable === true,
    required: data?.required === true,
    reason,
    manualTriggerId,
    storeUrl: String(data?.storeUrl || ""),
    title: String(data?.title || ""),
    message: String(data?.message || ""),
    updateLabel: String(data?.updateLabel || ""),
    laterLabel: String(data?.laterLabel || ""),
    latestVersion,
    latestBuild,
    intervalHours,
    fingerprint,
  };
}

export function resolveAppUpdateDecision(config, identity, options = {}) {
  if (!config?.enabled || !config?.storeUrl) return null;

  let versionUpdate = false;
  if (config.latestVersion) {
    const comparison = compareAppVersions(identity.version, config.latestVersion);
    versionUpdate = comparison < 0 ||
      (comparison === 0 && config.latestBuild > 0 && numericBuild(identity.build) < config.latestBuild);
  } else {
    versionUpdate = config.reason === "version" && config.updateAvailable === true;
  }

  const manualUpdate = !versionUpdate && config.reason === "manual" && config.updateAvailable === true;
  if (!versionUpdate && !manualUpdate) return null;

  if (!config.required) {
    if (manualUpdate && config.manualTriggerId && options.handledManualTriggerId === config.manualTriggerId) return null;
    if (options.snooze?.fingerprint === config.fingerprint && Number(options.snooze.until || 0) > Number(options.now || Date.now())) return null;
  }

  return {
    required: config.required,
    reason: manualUpdate ? "manual" : "version",
    manualTriggerId: config.manualTriggerId,
    storeUrl: config.storeUrl,
    title: config.title,
    message: config.message,
    updateLabel: config.updateLabel,
    laterLabel: config.laterLabel,
    fingerprint: config.fingerprint,
    checkIntervalHours: config.intervalHours,
  };
}

function cachedDecision(identity) {
  const cached = readJson(CACHE_KEY);
  if (!cached || !sameAppIdentity(cached.identity, identity)) return null;
  return resolveAppUpdateDecision(cached.config, identity, {
    handledManualTriggerId: readValue(MANUAL_TRIGGER_KEY) || "",
    snooze: readJson(SNOOZE_KEY),
  });
}

function networkCheckDue(identity, language, now) {
  const cached = readJson(CACHE_KEY);
  const lastFailureAt = readNumber(FAILURE_KEY, 0);
  if (!cached || !sameAppIdentity(cached.identity, identity)) {
    return now - lastFailureAt >= FAILURE_RETRY_MS;
  }
  const normalizedLanguage = language === "en" ? "en" : "tr";
  if (cached.language !== normalizedLanguage) return now - lastFailureAt >= FAILURE_RETRY_MS;
  const intervalHours = readNumber(INTERVAL_KEY, DEFAULT_INTERVAL_HOURS);
  if (intervalHours === 0) return true;
  const lastSuccessAt = readNumber(SUCCESS_KEY, 0);
  if (now - lastSuccessAt < intervalHours * 60 * 60 * 1000) return false;
  return now - lastFailureAt >= FAILURE_RETRY_MS;
}

async function fetchUpdateConfig(identity, language) {
  const query = new URLSearchParams({
    platform: identity.platform,
    version: identity.version,
    build: identity.build,
    language: language === "en" ? "en" : "tr",
  });
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE_URL}/app-update?${query.toString()}`, {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Update check failed: ${response.status}`);
    return await response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function performCheck(language, { forceNetwork = false } = {}) {
  if (!Capacitor.isNativePlatform()) return { status: "skipped", update: null };

  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") return { status: "skipped", update: null };

  let appInfo;
  try {
    appInfo = await CapacitorApp.getInfo();
  } catch (error) {
    writeValue(FAILURE_KEY, Date.now());
    console.warn("[app-update] native app info unavailable", error?.name || "unknown_error");
    return { status: "failure", update: null };
  }

  const identity = appIdentity(platform, appInfo);
  const localDecision = cachedDecision(identity);
  const now = Date.now();
  if (!forceNetwork && !networkCheckDue(identity, language, now)) {
    return { status: "cached", update: localDecision };
  }

  try {
    const data = await fetchUpdateConfig(identity, language);
    const config = normalizeConfig(data, identity);
    writeValue(INTERVAL_KEY, config.intervalHours);
    const successfulAt = Date.now();
    writeValue(SUCCESS_KEY, successfulAt);
    removeValue(FAILURE_KEY);
    writeJson(CACHE_KEY, {
      identity,
      language: language === "en" ? "en" : "tr",
      config,
      savedAt: successfulAt,
    });
    const snooze = readJson(SNOOZE_KEY);
    if (snooze?.fingerprint && snooze.fingerprint !== config.fingerprint) {
      removeValue(SNOOZE_KEY);
    }
    if (!config.enabled) {
      removeValue(SNOOZE_KEY);
      removeValue(MANUAL_TRIGGER_KEY);
    }
    const update = resolveAppUpdateDecision(config, identity, {
      handledManualTriggerId: readValue(MANUAL_TRIGGER_KEY) || "",
      snooze: config.enabled ? readJson(SNOOZE_KEY) : null,
      now,
    });
    return { status: "success", update };
  } catch (error) {
    writeValue(FAILURE_KEY, now);
    console.warn("[app-update] update check failed", error?.name || "unknown_error");
    // Ağ hatası yeni bir güncelleme kararı değildir. Eski optional/forced
    // popupı yeniden göstermek yerine kontrollü retry kararını çağırana bırak.
    return { status: "failure", update: null };
  }
}

export function checkAppUpdate(language, options = {}) {
  if (!activeCheckPromise) {
    activeCheckPromise = performCheck(language, options)
      .finally(() => { activeCheckPromise = null; });
  }
  return activeCheckPromise;
}

export function snoozeAppUpdate(update) {
  if (!update?.fingerprint || update.required) return;
  const hours = normalizedInterval(update.checkIntervalHours);
  writeJson(SNOOZE_KEY, {
    fingerprint: update.fingerprint,
    // Test/derhal-kontrol modunda (0) yalnız mevcut gösterimi kapat; bir
    // sonraki app activation güncel backend kararını tekrar değerlendirsin.
    until: Date.now() + hours * 60 * 60 * 1000,
  });
}

export function markManualTriggerHandled(triggerId) {
  if (triggerId) writeValue(MANUAL_TRIGGER_KEY, triggerId);
}

export function openAppStore(storeUrl) {
  if (!storeUrl) return;
  const link = document.createElement("a");
  link.href = storeUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}
